import {cn} from 'platform/shared-tw';
import {match} from 'ts-pattern';

import {useRef} from 'react';
import {useTranslation} from 'react-i18next';

import {Tabs, TabsContent, TabsList, TabsTrigger} from '../Tabs/Tabs';
import type {
  CarDiagramProps,
  DiagramFilters,
  DiagramVariant,
  ViewPartVariants,
} from './CarDiagram.types';
import {useDiagramFilters} from './hooks/useDiagramFilters';
import {usePan} from './hooks/usePan';
import {useZoom} from './hooks/useZoom';
import {Ruler} from './utility/Ruler';
import {ElectricView} from './views/ElectricView';
import {EngineView} from './views/EngineView';
import {ExteriorView} from './views/ExteriorView';
import {InteriorView} from './views/InteriorView';
import {MechanicsView} from './views/MechanicsView';

const CAR_WIDTH = 1342;
const CAR_HEIGHT = 502;

const DAMAGE_COLOR = 'var(--color-mira-red-50)';
const SERVICE_COLOR = 'var(--color-mira-orange-50)';
const SUGGESTIONS_COLOR = 'var(--color-mira-purple-50)';

// ============================================================
// Helpers
// ============================================================

function applyFilters(
  variants: ViewPartVariants,
  filters: Pick<DiagramFilters, 'damages' | 'service' | 'suggestions'>
): ViewPartVariants {
  const result: Record<string, DiagramVariant> = {};
  for (const [partId, variant] of Object.entries(variants)) {
    result[partId] = match(variant)
      .with('damage', () => (filters.damages ? 'damage' : 'default'))
      .with('service', () => (filters.service ? 'service' : 'default'))
      .with('suggestions', () => (filters.suggestions ? 'suggestions' : 'default'))
      .otherwise(() => 'default');
  }
  return result;
}

function getIndicatorColor(variants: ViewPartVariants): string | undefined {
  let hasDamage = false;
  let hasService = false;
  let hasSuggestions = false;

  for (const v of Object.values(variants)) {
    if (v === 'damage') {
      hasDamage = true;
    }
    if (v === 'service') {
      hasService = true;
    }
    if (v === 'suggestions') {
      hasSuggestions = true;
    }
  }

  if (hasDamage) {
    return DAMAGE_COLOR;
  }
  if (hasService) {
    return SERVICE_COLOR;
  }
  if (hasSuggestions) {
    return SUGGESTIONS_COLOR;
  }
  return undefined;
}

// ============================================================
// FilterToggle
// ============================================================

interface FilterToggleProps {
  label: string;
  sublabel: string;
  active: boolean;
  activeColor: string;
  onToggle: () => void;
}

function FilterToggle(props: FilterToggleProps) {
  return (
    <button
      type="button"
      onClick={props.onToggle}
      className="flex cursor-pointer items-center gap-2"
    >
      {/* Toggle pill */}
      <div
        className={cn(
          'relative h-5 w-9 rounded-full transition-colors duration-200',
          props.active ? '' : 'bg-gray-200'
        )}
        style={props.active ? {backgroundColor: props.activeColor} : undefined}
      >
        <div
          className={cn(
            'absolute top-0.5 size-4 rounded-full bg-white shadow-sm transition-transform duration-200',
            props.active ? 'translate-x-4' : 'translate-x-0.5'
          )}
        />
      </div>
      {/* Labels */}
      <div className="flex flex-col items-start">
        <span className="text-xs font-medium text-(--color-text-primary)">{props.label}</span>
        <span className="text-xs text-(--color-text-secondary)">{props.sublabel}</span>
      </div>
    </button>
  );
}

// ============================================================
// ZoomControls
// ============================================================

interface ZoomControlsProps {
  onZoomIn: () => void;
  onZoomOut: () => void;
}

function ZoomControls(props: ZoomControlsProps) {
  const {t} = useTranslation();

  return (
    <div className="flex items-center gap-2">
      <button
        type="button"
        onClick={props.onZoomIn}
        aria-label={t('ui.carDiagram.zoomIn')}
        className="flex size-9 cursor-pointer items-center justify-center rounded-full bg-gray-200 text-slate-500 transition-colors hover:bg-gray-300"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <line x1="12" y1="5" x2="12" y2="19" />
          <line x1="5" y1="12" x2="19" y2="12" />
        </svg>
      </button>
      <button
        type="button"
        onClick={props.onZoomOut}
        aria-label={t('ui.carDiagram.zoomOut')}
        className="flex size-9 cursor-pointer items-center justify-center rounded-full bg-gray-200 text-slate-500 transition-colors hover:bg-gray-300"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <line x1="5" y1="12" x2="19" y2="12" />
        </svg>
      </button>
    </div>
  );
}

// ============================================================
// DiagramPanel — shared tab panel content layout
// ============================================================

interface DiagramPanelProps {
  view: React.ReactNode;
  filters: DiagramFilters;
  zoom: number;
  onToggleFilter: (key: keyof DiagramFilters) => void;
  onZoomIn: () => void;
  onZoomOut: () => void;
}

const LEFT_RULER_WIDTH = 80;
const CAR_PADDING_Y = 56;
const BOTTOM_RULERS_HEIGHT = 88;
const CAR_PADDING_X = 64;

// The outer diagram area aspect ratio includes left ruler + car + bottom rulers.
// This drives the full panel height responsively from its container width.
const DIAGRAM_ASPECT_RATIO = `${CAR_WIDTH + LEFT_RULER_WIDTH} / ${CAR_HEIGHT + CAR_PADDING_Y * 2 + BOTTOM_RULERS_HEIGHT}`;

function DiagramPanel(props: DiagramPanelProps) {
  const {t} = useTranslation();
  // Each panel owns its own scroll container ref so panning is independent per tab.
  const containerRef = useRef<HTMLDivElement>(null);
  const pan = usePan(containerRef);

  return (
    <div className="bg-(--color-car-floor)">
      {/* Controls row */}
      <div className="flex items-center justify-between px-6 py-4">
        <div className="flex flex-wrap items-center gap-6">
          <FilterToggle
            label={t('ui.carDiagram.filter.damages.label')}
            sublabel={t('ui.carDiagram.filter.damages.sublabel')}
            active={props.filters.damages}
            activeColor={DAMAGE_COLOR}
            onToggle={() => props.onToggleFilter('damages')}
          />
          <FilterToggle
            label={t('ui.carDiagram.filter.service.label')}
            sublabel={t('ui.carDiagram.filter.service.sublabel')}
            active={props.filters.service}
            activeColor={SERVICE_COLOR}
            onToggle={() => props.onToggleFilter('service')}
          />
          <FilterToggle
            label={t('ui.carDiagram.filter.suggestions.label')}
            sublabel={t('ui.carDiagram.filter.suggestions.sublabel')}
            active={props.filters.suggestions}
            activeColor={SUGGESTIONS_COLOR}
            onToggle={() => props.onToggleFilter('suggestions')}
          />
          {/* TODO: re-enable Dimensions filter toggle once real vehicle dimension data is wired up
          <FilterToggle
            label={t('ui.carDiagram.filter.dimensions.label')}
            sublabel={t('ui.carDiagram.filter.dimensions.sublabel')}
            active={props.filters.dimensions}
            activeColor="var(--color-mira-gray-50)"
            onToggle={() => props.onToggleFilter('dimensions')}
          />
          */}
        </div>
        <ZoomControls onZoomIn={props.onZoomIn} onZoomOut={props.onZoomOut} />
      </div>

      {/* Diagram area — responsive height via aspect-ratio */}
      <div className="relative w-full" style={{aspectRatio: DIAGRAM_ASPECT_RATIO}}>
        {/* TODO: Left vertical rulers — shown when dimensions toggle is enabled.
            Dimension values are hardcoded placeholders; replace with real vehicle data
            from the API before enabling the dimensions filter toggle above. */}
        {props.filters.dimensions && (
          <div
            className="absolute top-0 left-0 flex flex-row items-center gap-2 pr-3"
            style={{
              width: LEFT_RULER_WIDTH,
              top: 0,
              bottom: BOTTOM_RULERS_HEIGHT,
            }}
          >
            <Ruler
              title={t('ui.carDiagram.dimension.wheelbase')}
              length="190.0 cm"
              orientation="vertical"
              className="w-8 h-full"
            />
            <Ruler
              title={t('ui.carDiagram.dimension.width')}
              length="215.7 cm"
              orientation="vertical"
              className="flex-1"
              style={{height: `calc(100% - ${CAR_PADDING_Y * 2}px)`}}
            />
          </div>
        )}

        {/* Car scroll surface — positioned right of left ruler, above bottom rulers */}
        <div
          ref={containerRef}
          className="absolute overflow-auto select-none [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
          style={{
            top: 0,
            left: props.filters.dimensions ? LEFT_RULER_WIDTH : 0,
            right: 0,
            bottom: props.filters.dimensions ? BOTTOM_RULERS_HEIGHT : 0,
            cursor: pan.cursor,
          }}
          onMouseDown={pan.onMouseDown}
          onMouseMove={pan.onMouseMove}
          onMouseUp={pan.onMouseUp}
          onMouseLeave={pan.onMouseLeave}
        >
          {/* Car content — padded on all sides so edges are always reachable when panning */}
          <div
            style={{
              display: 'inline-flex',
              minWidth: '100%',
              minHeight: '100%',
              alignItems: 'center',
              justifyContent: 'safe center',
            }}
          >
            <div
              style={{
                paddingTop: CAR_PADDING_Y * props.zoom,
                paddingBottom: CAR_PADDING_Y * props.zoom,
                paddingLeft: CAR_PADDING_X * props.zoom,
                paddingRight: CAR_PADDING_X * props.zoom,
                flexShrink: 0,
              }}
            >
              <div
                style={{
                  transform: `scale(${props.zoom})`,
                  transformOrigin: 'top left',
                  width: CAR_WIDTH,
                  height: CAR_HEIGHT,
                }}
              >
                {props.view}
              </div>
            </div>
          </div>
        </div>

        {/* TODO: Bottom horizontal rulers — shown when dimensions toggle is enabled.
            Dimension values are hardcoded placeholders; replace with real vehicle data
            from the API before enabling the dimensions filter toggle above. */}
        {props.filters.dimensions && (
          <div
            className="absolute right-0 left-0 flex flex-col items-center justify-center gap-3"
            style={{
              bottom: 0,
              height: BOTTOM_RULERS_HEIGHT,
              paddingLeft: LEFT_RULER_WIDTH,
            }}
          >
            <Ruler
              title={t('ui.carDiagram.dimension.wheelbase')}
              length="399.0 cm"
              orientation="horizontal"
              className="w-[55%]"
            />
            <Ruler
              title={t('ui.carDiagram.dimension.length')}
              length="506.0 cm"
              orientation="horizontal"
              className="w-[70%]"
            />
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================
// CarDiagram (root)
// ============================================================

export function CarDiagram(props: CarDiagramProps) {
  const {t} = useTranslation();
  const diagramFilters = useDiagramFilters();
  const zoomState = useZoom();

  const filteredData = {
    exterior: applyFilters(props.data.exterior, diagramFilters.filters),
    interior: applyFilters(props.data.interior, diagramFilters.filters),
    mechanics: applyFilters(props.data.mechanics, diagramFilters.filters),
    engine: applyFilters(props.data.engine, diagramFilters.filters),
    electric: applyFilters(props.data.electric, diagramFilters.filters),
  };

  return (
    <div className="w-full">
      <Tabs defaultValue="exterior">
        {/* Tab list — centered */}
        <div className="border-b border-(--color-mira-gray-10) flex justify-center px-6">
          <TabsList>
            <TabsTrigger value="exterior" indicatorColor={getIndicatorColor(props.data.exterior)}>
              {t('ui.carDiagram.tab.exterior')}
            </TabsTrigger>
            <TabsTrigger value="interior" indicatorColor={getIndicatorColor(props.data.interior)}>
              {t('ui.carDiagram.tab.interior')}
            </TabsTrigger>
            <TabsTrigger value="engine" indicatorColor={getIndicatorColor(props.data.engine)}>
              {t('ui.carDiagram.tab.engine')}
            </TabsTrigger>
            <TabsTrigger value="mechanics" indicatorColor={getIndicatorColor(props.data.mechanics)}>
              {t('ui.carDiagram.tab.mechanics')}
            </TabsTrigger>
            {/* TODO: re-enable E-mechanics tab once electric vehicle diagram parts are finalised
            <TabsTrigger value="electric" indicatorColor={getIndicatorColor(props.data.electric)}>
              E-mechanics
            </TabsTrigger>
            */}
          </TabsList>
        </div>

        {/* Tab panels */}
        <TabsContent value="exterior">
          <DiagramPanel
            view={
              <ExteriorView partVariants={filteredData.exterior} onPartClick={props.onPartClick} />
            }
            filters={diagramFilters.filters}
            zoom={zoomState.zoom}
            onToggleFilter={diagramFilters.toggleFilter}
            onZoomIn={zoomState.zoomIn}
            onZoomOut={zoomState.zoomOut}
          />
        </TabsContent>

        <TabsContent value="interior">
          <DiagramPanel
            view={
              <InteriorView partVariants={filteredData.interior} onPartClick={props.onPartClick} />
            }
            filters={diagramFilters.filters}
            zoom={zoomState.zoom}
            onToggleFilter={diagramFilters.toggleFilter}
            onZoomIn={zoomState.zoomIn}
            onZoomOut={zoomState.zoomOut}
          />
        </TabsContent>

        <TabsContent value="mechanics">
          <DiagramPanel
            view={
              <MechanicsView
                partVariants={filteredData.mechanics}
                onPartClick={props.onPartClick}
              />
            }
            filters={diagramFilters.filters}
            zoom={zoomState.zoom}
            onToggleFilter={diagramFilters.toggleFilter}
            onZoomIn={zoomState.zoomIn}
            onZoomOut={zoomState.zoomOut}
          />
        </TabsContent>

        <TabsContent value="engine">
          <DiagramPanel
            view={<EngineView partVariants={filteredData.engine} onPartClick={props.onPartClick} />}
            filters={diagramFilters.filters}
            zoom={zoomState.zoom}
            onToggleFilter={diagramFilters.toggleFilter}
            onZoomIn={zoomState.zoomIn}
            onZoomOut={zoomState.zoomOut}
          />
        </TabsContent>

        <TabsContent value="electric">
          <DiagramPanel
            view={
              <ElectricView partVariants={filteredData.electric} onPartClick={props.onPartClick} />
            }
            filters={diagramFilters.filters}
            zoom={zoomState.zoom}
            onToggleFilter={diagramFilters.toggleFilter}
            onZoomIn={zoomState.zoomIn}
            onZoomOut={zoomState.zoomOut}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
